import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, storeToken, clearToken } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  BarChart3, Users, FileText, Activity, Shield,
  Search, ChevronLeft, ChevronRight, Crown,
  ArrowLeft, Trash2, Eye, LogOut, RefreshCw,
  TrendingUp, Gamepad2, MessageCircle, BookOpen,
  Download, Database, ShieldAlert, Zap, Plus,
  Edit, Globe, AlertTriangle, Settings, Ban, CheckCircle2,
  Gem, Heart, Brain, Dumbbell
} from "lucide-react";
import { toast } from "sonner";
import { devError } from "../utils/devLogger";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area } from "recharts";

const CHART_COLORS = ["#38bdf8", "#a78bfa", "#34d399", "#fbbf24", "#f472b6", "#22d3ee"];

// ==================== COMMAND CENTER TAB ====================
const CommandCenterTab = ({ adminAxios }) => {
  const [engagement, setEngagement] = useState(null);
  const [subs, setSubs] = useState(null);
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [eRes, sRes, pRes] = await Promise.all([
          adminAxios().get(`${API}/admin/analytics/engagement`),
          adminAxios().get(`${API}/admin/analytics/subscriptions`),
          adminAxios().get(`${API}/admin/analytics/popular-plans`),
        ]);
        setEngagement(eRes.data);
        setSubs(sRes.data);
        setPlans(pRes.data);
      } catch (err) {
        toast.error("Failed to load analytics");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [adminAxios]);

  if (loading) return <div className="text-center py-8 text-slate-400">Loading Command Center...</div>;

  return (
    <div className="space-y-6" data-testid="admin-command-center">
      <h1 className="text-2xl font-light flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
        <Zap className="w-6 h-6 text-amber-400" strokeWidth={1.5} /> LumiFit Command Center
      </h1>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Daily Active", value: engagement?.dau || 0, icon: Users, color: "text-sky-400", sub: "users today" },
          { label: "Weekly Active", value: engagement?.wau || 0, icon: TrendingUp, color: "text-emerald-400", sub: "last 7 days" },
          { label: "MRR", value: `$${subs?.mrr || 0}`, icon: Crown, color: "text-amber-400", sub: "monthly recurring" },
          { label: "Conversion", value: `${subs?.conversion_rate || 0}%`, icon: Gem, color: "text-violet-400", sub: "free → paid" },
        ].map(s => (
          <div key={s.label} className="bg-slate-800 rounded-xl p-5 border border-slate-700" data-testid={`kpi-${s.label.toLowerCase().replace(/\s/g,'-')}`}>
            <div className="flex items-center justify-between mb-3">
              <s.icon className={`w-5 h-5 ${s.color}`} strokeWidth={1.5} />
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">{s.sub}</span>
            </div>
            <p className="text-3xl font-light text-white" style={{ fontFamily: "Outfit" }}>{s.value}</p>
            <p className="text-xs text-slate-400 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* DAU Trend */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h3 className="text-sm text-slate-400 mb-4">Daily Active Users (14 Days)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={[...(engagement?.dau_trend || [])].reverse()}>
              <XAxis dataKey="date" tickFormatter={d => d.slice(5)} tick={{ fontSize: 10, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
              <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: "8px", fontSize: 12 }} />
              <Area type="monotone" dataKey="count" stroke="#38bdf8" fill="#38bdf8" fillOpacity={0.15} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Tier Breakdown */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h3 className="text-sm text-slate-400 mb-4">Subscription Tiers</h3>
          <div className="flex items-center gap-6">
            <ResponsiveContainer width={160} height={160}>
              <PieChart>
                <Pie data={subs?.tier_breakdown || []} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={3}>
                  {(subs?.tier_breakdown || []).map((entry, i) => (
                    <Cell key={entry.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: "8px", fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2">
              {(subs?.tier_breakdown || []).map((t, i) => (
                <div key={t.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded" style={{ background: CHART_COLORS[i] }} />
                  <span className="text-sm text-slate-300">{t.name}</span>
                  <span className="text-sm text-slate-500 ml-auto">{t.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Feature Usage + Popular Plans */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h3 className="text-sm text-slate-400 mb-4">Feature Usage (Last 7 Days)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={Object.entries(engagement?.feature_usage || {}).map(([k, v]) => ({ name: k.replace(/_/g, " "), value: v }))}>
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
              <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: "8px", fontSize: 12 }} />
              <Bar dataKey="value" fill="#818cf8" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h3 className="text-sm text-slate-400 mb-4">Popular Exercise Categories</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={plans?.popular_categories || []} layout="vertical">
              <XAxis type="number" tick={{ fontSize: 10, fill: "#64748b" }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#94a3b8" }} width={80} />
              <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: "8px", fontSize: 12 }} />
              <Bar dataKey="count" fill="#34d399" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Subscription Growth */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h3 className="text-sm text-slate-400 mb-4">New Subscriptions (Last 30 Days)</h3>
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={[...(subs?.sub_trend || [])].reverse()}>
            <XAxis dataKey="date" tickFormatter={d => d.slice(5)} tick={{ fontSize: 9, fill: "#64748b" }} />
            <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
            <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: "8px", fontSize: 12 }} />
            <Line type="monotone" dataKey="count" stroke="#fbbf24" strokeWidth={2} dot={{ r: 2, fill: "#fbbf24" }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Revenue Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "ARR", value: `$${subs?.arr || 0}` },
          { label: "Pro Users", value: subs?.pro_count || 0 },
          { label: "Diamond Users", value: subs?.diamond_count || 0 },
          { label: "Retention Rate", value: `${engagement?.retention_rate || 0}%` },
        ].map(s => (
          <div key={s.label} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50 text-center">
            <p className="text-xl font-light text-white" style={{ fontFamily: "Outfit" }}>{s.value}</p>
            <p className="text-xs text-slate-500 mt-1">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== ENHANCED NEMOCLAW TAB ====================
const NemoclawTab = ({ adminAxios }) => {
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blockIp, setBlockIp] = useState("");
  const [blockReason, setBlockReason] = useState("");
  const [configEditing, setConfigEditing] = useState(false);
  const [editRecipients, setEditRecipients] = useState("");
  const [editBlockThreshold, setEditBlockThreshold] = useState(50);
  const [editAlertThreshold, setEditAlertThreshold] = useState(25);

  const fetchAll = useCallback(async () => {
    try {
      const [sRes, eRes, cRes] = await Promise.all([
        adminAxios().get(`${API}/admin/nemoclaw`),
        adminAxios().get(`${API}/admin/nemoclaw/events?limit=30`),
        adminAxios().get(`${API}/admin/nemoclaw/config`),
      ]);
      setStats(sRes.data);
      setEvents(eRes.data.events);
      setConfig(cRes.data);
    } catch (err) {
      toast.error("Failed to load Nemoclaw data");
    } finally {
      setLoading(false);
    }
  }, [adminAxios]);

  useEffect(() => { fetchAll(); const iv = setInterval(fetchAll, 30000); return () => clearInterval(iv); }, [fetchAll]);

  const handleBlockIp = async () => {
    if (!blockIp.trim()) return;
    try {
      await adminAxios().post(`${API}/admin/nemoclaw/block-ip`, { ip: blockIp.trim(), reason: blockReason || "Manual block" });
      toast.success(`IP ${blockIp} blocked`);
      setBlockIp(""); setBlockReason("");
      fetchAll();
    } catch (err) { toast.error("Failed to block IP"); }
  };

  const handleUnblockIp = async (ip) => {
    try {
      await adminAxios().post(`${API}/admin/nemoclaw/unblock-ip`, { ip });
      toast.success(`IP ${ip} unblocked`);
      fetchAll();
    } catch (err) { toast.error("Failed to unblock IP"); }
  };

  const handleSaveConfig = async () => {
    try {
      await adminAxios().put(`${API}/admin/nemoclaw/config`, {
        alert_recipients: editRecipients.split(",").map(e => e.trim()).filter(Boolean),
        block_threshold: editBlockThreshold,
        alert_threshold: editAlertThreshold,
      });
      toast.success("Configuration saved");
      setConfigEditing(false);
      fetchAll();
    } catch (err) { toast.error("Failed to save config"); }
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Loading Nemoclaw...</div>;

  return (
    <div className="space-y-6" data-testid="admin-nemoclaw-content">
      <h1 className="text-2xl font-light flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
        <ShieldAlert className="w-6 h-6 text-red-400" strokeWidth={1.5} /> Nemoclaw Security Agent
      </h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "IP Checks", value: stats?.total_ip_checks || 0, color: "text-sky-400" },
          { label: "Blocked IPs", value: stats?.blocked_ips || 0, color: "text-red-400" },
          { label: "Auto Reports", value: stats?.auto_reports || 0, color: "text-amber-400" },
          { label: "Alerts Sent", value: stats?.alerts_sent || 0, color: "text-emerald-400" },
        ].map(s => (
          <div key={s.label} className="bg-slate-800 rounded-xl p-4 border border-slate-700">
            <p className={`text-2xl font-light ${s.color}`} style={{ fontFamily: "Outfit" }}>{s.value}</p>
            <p className="text-xs text-slate-400">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Manual IP Block/Allow */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h3 className="text-sm text-slate-400 mb-4 flex items-center gap-2"><Ban className="w-4 h-4" /> Manual IP Management</h3>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Input value={blockIp} onChange={e => setBlockIp(e.target.value)} placeholder="IP address (e.g. 1.2.3.4)"
              className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500" data-testid="nemoclaw-block-ip-input" />
          </div>
          <div className="flex-1">
            <Input value={blockReason} onChange={e => setBlockReason(e.target.value)} placeholder="Reason (optional)"
              className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500" />
          </div>
          <Button onClick={handleBlockIp} className="bg-red-500 hover:bg-red-600 text-sm" data-testid="nemoclaw-block-btn">
            <Ban className="w-4 h-4 mr-1" /> Block
          </Button>
        </div>
      </div>

      {/* Alert Configuration */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm text-slate-400 flex items-center gap-2"><Settings className="w-4 h-4" /> Alert Configuration</h3>
          {!configEditing && (
            <Button variant="ghost" size="sm" onClick={() => {
              setEditRecipients((config?.alert_recipients || []).join(", "));
              setEditBlockThreshold(config?.block_threshold || 50);
              setEditAlertThreshold(config?.alert_threshold || 25);
              setConfigEditing(true);
            }} className="text-sky-400 hover:text-sky-300" data-testid="nemoclaw-edit-config-btn">
              <Edit className="w-4 h-4 mr-1" /> Edit
            </Button>
          )}
        </div>
        {configEditing ? (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500">Alert recipients (comma separated)</label>
              <Input value={editRecipients} onChange={e => setEditRecipients(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="nemoclaw-config-recipients" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500">Block threshold (abuse score)</label>
                <Input type="number" value={editBlockThreshold} onChange={e => setEditBlockThreshold(Number(e.target.value))}
                  className="bg-slate-700 border-slate-600 text-white mt-1" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Alert threshold (abuse score)</label>
                <Input type="number" value={editAlertThreshold} onChange={e => setEditAlertThreshold(Number(e.target.value))}
                  className="bg-slate-700 border-slate-600 text-white mt-1" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSaveConfig} className="bg-emerald-500 hover:bg-emerald-600 text-sm" data-testid="nemoclaw-save-config-btn">Save</Button>
              <Button variant="ghost" onClick={() => setConfigEditing(false)} className="text-slate-400 text-sm">Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-300 space-y-1">
            <p><span className="text-slate-500">Recipients:</span> {(config?.alert_recipients || []).join(", ") || "None"}</p>
            <p><span className="text-slate-500">Block at:</span> score {">="} {config?.block_threshold || 50}</p>
            <p><span className="text-slate-500">Alert at:</span> score {">="} {config?.alert_threshold || 25}</p>
          </div>
        )}
      </div>

      {/* Event Log */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h3 className="text-sm text-slate-400 mb-4">Event Log (Recent)</h3>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {events.length === 0 && <p className="text-sm text-slate-500">No events recorded yet</p>}
          {events.map((ev, idx) => (
            <div key={`${ev.type}-${ev.time}-${idx}`} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-700/30 text-sm">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                ev.type === "ip_check" ? (ev.blocked ? "bg-red-400" : "bg-emerald-400") :
                ev.type === "auto_report" ? "bg-amber-400" : "bg-sky-400"
              }`} />
              <span className="text-slate-400 w-20 flex-shrink-0 text-xs">{ev.type.replace(/_/g, " ")}</span>
              <span className="text-white flex-1 truncate">
                {ev.ip ? `${ev.ip}${ev.score ? ` (score: ${ev.score})` : ""}${ev.country ? ` — ${ev.country}` : ""}` : ev.subject || "Alert"}
              </span>
              <span className="text-xs text-slate-500 flex-shrink-0">{ev.time ? new Date(ev.time).toLocaleString() : ""}</span>
              {ev.blocked && ev.ip && (
                <Button variant="ghost" size="sm" onClick={() => handleUnblockIp(ev.ip)} className="text-emerald-400 hover:text-emerald-300 text-xs h-6 px-2">
                  Allow
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Recent Blocks */}
      {stats?.recent_blocks?.length > 0 && (
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h3 className="text-sm text-slate-400 mb-4">Recently Blocked IPs</h3>
          <div className="space-y-2">
            {stats.recent_blocks.map((b) => (
              <div key={`block-${b.ip}-${b.checked_at}`} className="flex items-center justify-between px-3 py-2 rounded-lg bg-red-500/10 text-sm">
                <span className="text-white font-mono">{b.ip}</span>
                <span className="text-xs text-slate-500">{b.checked_at ? new Date(b.checked_at).toLocaleString() : ""}</span>
                <Button variant="ghost" size="sm" onClick={() => handleUnblockIp(b.ip)} className="text-emerald-400 text-xs h-6 px-2">Unblock</Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== BLOG MANAGEMENT TAB ====================
const BlogTab = ({ adminAxios }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", summary: "", category: "general", published: false });

  const fetchPosts = useCallback(async () => {
    try {
      const res = await adminAxios().get(`${API}/admin/blog/posts`);
      setPosts(res.data.posts || []);
    } catch (err) { toast.error("Failed to load blog posts"); }
    finally { setLoading(false); }
  }, [adminAxios]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    try {
      await adminAxios().post(`${API}/admin/blog/create`, form);
      toast.success("Blog post created");
      setCreating(false);
      setForm({ title: "", content: "", summary: "", category: "general", published: false });
      fetchPosts();
    } catch (err) { toast.error("Failed to create post"); }
  };

  const handleUpdate = async () => {
    try {
      await adminAxios().put(`${API}/admin/blog/posts/${editing}`, form);
      toast.success("Post updated");
      setEditing(null);
      fetchPosts();
    } catch (err) { toast.error("Failed to update post"); }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await adminAxios().delete(`${API}/admin/blog/posts/${postId}`);
      toast.success("Post deleted");
      fetchPosts();
    } catch (err) { toast.error("Failed to delete post"); }
  };

  const startEdit = (post) => {
    setForm({ title: post.title, content: post.content || "", summary: post.summary || "", category: post.category || "general", published: post.published || false });
    setEditing(post.id);
    setCreating(true);
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Loading blog...</div>;

  return (
    <div className="space-y-6" data-testid="admin-blog-content">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-light" style={{ fontFamily: "Outfit" }}>Blog Management</h1>
        <Button onClick={() => { setCreating(true); setEditing(null); setForm({ title: "", content: "", summary: "", category: "general", published: false }); }}
          className="bg-sky-500 hover:bg-sky-600 text-sm" data-testid="blog-create-btn">
          <Plus className="w-4 h-4 mr-1" /> New Post
        </Button>
      </div>

      {/* Create/Edit Form */}
      {creating && (
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 space-y-4">
          <h3 className="text-sm text-slate-400">{editing ? "Edit Post" : "Create New Post"}</h3>
          <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Post title"
            className="bg-slate-700 border-slate-600 text-white" data-testid="blog-title-input" />
          <Input value={form.summary} onChange={e => setForm(f => ({ ...f, summary: e.target.value }))} placeholder="Summary"
            className="bg-slate-700 border-slate-600 text-white" />
          <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} placeholder="Content (markdown)"
            rows={6} className="w-full bg-slate-700 border border-slate-600 rounded-lg p-3 text-white text-sm resize-y" data-testid="blog-content-input" />
          <div className="flex items-center gap-4">
            <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
              <option value="general">General</option>
              <option value="fitness">Fitness</option>
              <option value="cognitive">Cognitive</option>
              <option value="nutrition">Nutrition</option>
              <option value="mindfulness">Mindfulness</option>
            </select>
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" checked={form.published} onChange={e => setForm(f => ({ ...f, published: e.target.checked }))}
                className="rounded" />
              Published
            </label>
          </div>
          <div className="flex gap-2">
            <Button onClick={editing ? handleUpdate : handleCreate} className="bg-emerald-500 hover:bg-emerald-600 text-sm" data-testid="blog-save-btn">
              {editing ? "Update" : "Create"}
            </Button>
            <Button variant="ghost" onClick={() => { setCreating(false); setEditing(null); }} className="text-slate-400 text-sm">Cancel</Button>
          </div>
        </div>
      )}

      {/* Posts Table */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-700 text-left">
              <th className="px-4 py-3 text-xs text-slate-400 font-medium">Title</th>
              <th className="px-4 py-3 text-xs text-slate-400 font-medium">Category</th>
              <th className="px-4 py-3 text-xs text-slate-400 font-medium">Published</th>
              <th className="px-4 py-3 text-xs text-slate-400 font-medium">Views</th>
              <th className="px-4 py-3 text-xs text-slate-400 font-medium">Date</th>
              <th className="px-4 py-3 text-xs text-slate-400 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {posts.map(post => (
              <tr key={post.id} className="border-b border-slate-700/50 hover:bg-slate-750">
                <td className="px-4 py-3 text-sm text-white">{post.title}</td>
                <td className="px-4 py-3 text-xs text-slate-400">{post.category || "general"}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${post.published ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-600 text-slate-300"}`}>
                    {post.published ? "Live" : "Draft"}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-slate-400">{post.views || 0}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{post.created_at ? new Date(post.created_at).toLocaleDateString() : ""}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(post)} className="text-sky-400 hover:text-sky-300 h-7 px-2" data-testid={`blog-edit-${post.id}`}>
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(post.id)} className="text-red-400 hover:text-red-300 h-7 px-2" data-testid={`blog-delete-${post.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {posts.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">No blog posts yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// ==================== MAIN ADMIN PANEL ====================
const AdminPanel = () => {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [adminToken, setAdminToken] = useState(sessionStorage.getItem("admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [activeTab, setActiveTab] = useState("command");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [userTotal, setUserTotal] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [userPage, setUserPage] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activity, setActivity] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchRef = useRef(null);

  const adminAxios = useCallback(() => axios.create({ headers: { Authorization: `Bearer ${adminToken}` } }), [adminToken]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      setAdminToken(res.data.session_token);
      sessionStorage.setItem("admin_token", res.data.session_token);
      storeToken(res.data.session_token);
      setAuthenticated(true);
      toast.success("Welcome, Admin!");
    } catch (err) { toast.error(err.response?.data?.detail || "Login failed"); }
  };

  const handleLogout = () => { clearToken(); sessionStorage.removeItem("admin_token"); setAuthenticated(false); setAdminToken(""); };

  const fetchStats = useCallback(async () => {
    try { const res = await adminAxios().get(`${API}/admin/stats`); setStats(res.data); } catch (err) { devError("Stats", err); }
  }, [adminAxios]);

  const fetchUsers = useCallback(async (search = "", page = 0) => {
    try {
      const res = await adminAxios().get(`${API}/admin/users?search=${search}&skip=${page * 50}&limit=50`);
      setUsers(res.data.users); setUserTotal(res.data.total);
    } catch (err) { devError("Users", err); }
  }, [adminAxios]);

  const fetchActivity = useCallback(async () => {
    try { const res = await adminAxios().get(`${API}/admin/activity`); setActivity(res.data.activities || []); } catch (err) { devError("Activity", err); }
  }, [adminAxios]);

  const fetchUserDetail = async (userId) => {
    try { const res = await adminAxios().get(`${API}/admin/users/${userId}`); setSelectedUser(res.data); } catch (err) { toast.error("Failed to load user"); }
  };

  const grantSubscription = async (userId, planId, years = 1) => {
    try {
      await adminAxios().post(`${API}/admin/users/${userId}/subscription`, { plan_id: planId, years });
      toast.success(`${planId.includes("diamond") ? "Diamond" : "Pro"} granted`);
      fetchUserDetail(userId); fetchUsers(userSearch, userPage);
    } catch (err) { toast.error("Failed to grant subscription"); }
  };

  const revokeSubscription = async (userId) => {
    try { await adminAxios().delete(`${API}/admin/users/${userId}/subscription`); toast.success("Revoked"); fetchUserDetail(userId); fetchUsers(userSearch, userPage); }
    catch (err) { toast.error("Failed"); }
  };

  // Global user search with autocomplete
  const handleGlobalSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); setShowSearchDropdown(false); return; }
    try {
      const res = await adminAxios().get(`${API}/admin/users/search?q=${encodeURIComponent(q)}`);
      setSearchResults(res.data.results || []);
      setShowSearchDropdown(true);
    } catch (err) { setSearchResults([]); }
  };

  const selectSearchResult = (u) => {
    setShowSearchDropdown(false);
    setSearchQuery("");
    setActiveTab("users");
    fetchUserDetail(u.user_id);
  };

  // Close search dropdown on outside click
  useEffect(() => {
    const close = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearchDropdown(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => {
    if (adminToken) {
      adminAxios().get(`${API}/admin/stats`).then(() => setAuthenticated(true)).catch(() => { setAuthenticated(false); sessionStorage.removeItem("admin_token"); });
    }
  }, [adminToken, adminAxios]);

  useEffect(() => {
    if (!authenticated) return;
    if (activeTab === "users") fetchUsers(userSearch, userPage);
    else if (activeTab === "activity") fetchActivity();
    else if (activeTab === "dashboard") fetchStats();
  }, [activeTab, authenticated, fetchUsers, fetchActivity, fetchStats, userSearch, userPage]);

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4" data-testid="admin-login-page">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <Shield className="w-12 h-12 text-sky-400 mx-auto mb-3" strokeWidth={1.5} />
            <h1 className="text-2xl font-light text-white" style={{ fontFamily: "Outfit" }}>LumiFit Admin</h1>
            <p className="text-sm text-slate-400 mt-1">Project Management Portal</p>
          </div>
          <form onSubmit={handleLogin} className="bg-slate-800 rounded-2xl p-6 space-y-4 border border-slate-700">
            <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="Admin email"
              className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400" data-testid="admin-email" />
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password"
              className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400" data-testid="admin-password" />
            <Button type="submit" className="w-full bg-sky-500 hover:bg-sky-600" data-testid="admin-login-btn">Sign In</Button>
          </form>
          <button onClick={() => navigate("/")} className="text-slate-500 text-sm mt-4 block mx-auto hover:text-slate-300">Back to site</button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "command", label: "Command Center", icon: Zap },
    { id: "dashboard", label: "Overview", icon: BarChart3 },
    { id: "users", label: "Users", icon: Users },
    { id: "activity", label: "Activity", icon: Activity },
    { id: "blog", label: "Blog", icon: BookOpen },
    { id: "export", label: "Data Export", icon: Database },
    { id: "nemoclaw", label: "Nemoclaw", icon: ShieldAlert },
  ];

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="admin-panel">
      {/* Sidebar */}
      <div className="fixed left-0 top-0 h-full w-56 bg-slate-800 border-r border-slate-700 flex flex-col z-20">
        <div className="p-5 border-b border-slate-700">
          <h2 className="text-lg font-medium text-sky-400" style={{ fontFamily: "Outfit" }}>LumiFit</h2>
          <p className="text-xs text-slate-400">Admin Panel</p>
        </div>

        {/* Global Search */}
        <div className="px-3 pt-3 relative" ref={searchRef}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
            <Input value={searchQuery} onChange={e => handleGlobalSearch(e.target.value)} placeholder="Find user..."
              className="pl-9 h-8 bg-slate-700 border-slate-600 text-white text-xs placeholder:text-slate-500" data-testid="admin-global-search" />
          </div>
          {showSearchDropdown && searchResults.length > 0 && (
            <div className="absolute left-3 right-3 top-full mt-1 bg-slate-700 rounded-lg border border-slate-600 shadow-xl z-30 max-h-60 overflow-y-auto" data-testid="search-dropdown">
              {searchResults.map(u => (
                <button key={u.user_id} onClick={() => selectSearchResult(u)}
                  className="w-full px-3 py-2 text-left hover:bg-slate-600 transition-colors first:rounded-t-lg last:rounded-b-lg">
                  <p className="text-sm text-white truncate">{u.name || "No name"}</p>
                  <p className="text-xs text-slate-400 truncate">{u.email}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSelectedUser(null); }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all ${
                activeTab === tab.id ? "bg-sky-500/20 text-sky-400" : "text-slate-400 hover:text-white hover:bg-slate-700"
              }`} data-testid={`admin-tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" strokeWidth={1.5} />
              {tab.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-700">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-slate-400 hover:text-red-400 hover:bg-slate-700 transition-all" data-testid="admin-logout">
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Logout
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="ml-56 p-8">
        {/* Command Center */}
        {activeTab === "command" && <CommandCenterTab adminAxios={adminAxios} />}

        {/* Dashboard Overview */}
        {activeTab === "dashboard" && stats && (
          <div className="space-y-6" data-testid="admin-dashboard-content">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-light" style={{ fontFamily: "Outfit" }}>Dashboard Overview</h1>
              <Button variant="ghost" size="sm" onClick={fetchStats} className="text-slate-400 hover:text-white">
                <RefreshCw className="w-4 h-4 mr-1" /> Refresh
              </Button>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Total Users", value: stats.total_users, icon: Users, color: "text-sky-400" },
                { label: "New This Week", value: stats.new_users_week, icon: TrendingUp, color: "text-emerald-400" },
                { label: "Pro Subscribers", value: stats.active_subscriptions, icon: Crown, color: "text-amber-400" },
                { label: "Blog Posts", value: stats.total_blog_posts, icon: BookOpen, color: "text-indigo-400" },
                { label: "Today's Questionnaires", value: stats.today_questionnaires, icon: FileText, color: "text-cyan-400" },
                { label: "Total Training Plans", value: stats.total_training_plans, icon: Activity, color: "text-rose-400" },
                { label: "Game Sessions", value: stats.total_game_results, icon: Gamepad2, color: "text-violet-400" },
                { label: "Messages Sent", value: stats.total_messages, icon: MessageCircle, color: "text-orange-400" },
              ].map((stat) => (
                <div key={stat.label} className="bg-slate-800 rounded-xl p-5 border border-slate-700">
                  <stat.icon className={`w-5 h-5 ${stat.color} mb-3`} strokeWidth={1.5} />
                  <p className="text-2xl font-light text-white" style={{ fontFamily: "Outfit" }}>{stat.value}</p>
                  <p className="text-xs text-slate-400 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-sm text-slate-400 mb-4">Signups (Last 14 Days)</h3>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={[...(stats.daily_signups || [])].reverse()}>
                  <XAxis dataKey="date" tickFormatter={d => d.slice(5)} tick={{ fontSize: 10, fill: "#64748b" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
                  <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: "8px", fontSize: 12 }} />
                  <Bar dataKey="count" fill="#38bdf8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && !selectedUser && (
          <div className="space-y-4" data-testid="admin-users-content">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-light" style={{ fontFamily: "Outfit" }}>User Management</h1>
              <span className="text-sm text-slate-400">{userTotal} total users</span>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={userSearch} onChange={e => { setUserSearch(e.target.value); setUserPage(0); }}
                placeholder="Search by name or email..." className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" data-testid="user-search" />
            </div>
            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden" data-testid="admin-user-list">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700 text-left">
                    <th className="px-4 py-3 text-xs text-slate-400 font-medium">User ID</th>
                    <th className="px-4 py-3 text-xs text-slate-400 font-medium">Name</th>
                    <th className="px-4 py-3 text-xs text-slate-400 font-medium">Email</th>
                    <th className="px-4 py-3 text-xs text-slate-400 font-medium">Joined</th>
                    <th className="px-4 py-3 text-xs text-slate-400 font-medium">Plan</th>
                    <th className="px-4 py-3 text-xs text-slate-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.user_id} className="border-b border-slate-700/50 hover:bg-slate-750">
                      <td className="px-4 py-3 text-xs text-slate-500 font-mono">{u.user_id}</td>
                      <td className="px-4 py-3 text-sm text-white">{u.name || "—"}</td>
                      <td className="px-4 py-3 text-sm text-slate-400">{u.email}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          u.subscription === "active"
                            ? (u.plan?.includes("diamond") ? "bg-violet-500/20 text-violet-400" : "bg-amber-500/20 text-amber-400")
                            : "bg-slate-600 text-slate-300"
                        }`}>
                          {u.subscription === "active" ? (u.plan?.includes("diamond") ? "Diamond" : "Pro") : "Free"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => fetchUserDetail(u.user_id)} className="text-sky-400 hover:text-sky-300" data-testid={`view-user-${u.user_id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {u.subscription !== "active" && (
                            <>
                              <Button size="sm" onClick={() => grantSubscription(u.user_id, "yearly", 1)} className="h-7 px-2 bg-amber-500/80 hover:bg-amber-500 text-[11px] text-white" data-testid={`quick-grant-pro-${u.user_id}`}>
                                <Crown className="w-3 h-3 mr-0.5" /> Pro
                              </Button>
                              <Button size="sm" onClick={() => grantSubscription(u.user_id, "diamond_yearly", 1)} className="h-7 px-2 bg-violet-500/80 hover:bg-violet-500 text-[11px] text-white" data-testid={`quick-grant-diamond-${u.user_id}`}>
                                <Crown className="w-3 h-3 mr-0.5" /> Diamond
                              </Button>
                            </>
                          )}
                          {u.subscription === "active" && !u.plan?.includes("diamond") && (
                            <Button size="sm" onClick={() => grantSubscription(u.user_id, "diamond_yearly", 1)} className="h-7 px-2 bg-violet-500/80 hover:bg-violet-500 text-[11px] text-white">
                              <Crown className="w-3 h-3 mr-0.5" /> Diamond
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" disabled={userPage === 0} onClick={() => setUserPage(p => p - 1)} className="text-slate-400">
                <ChevronLeft className="w-4 h-4" /> Previous
              </Button>
              <span className="text-xs text-slate-500">Page {userPage + 1}</span>
              <Button variant="ghost" size="sm" disabled={(userPage + 1) * 50 >= userTotal} onClick={() => setUserPage(p => p + 1)} className="text-slate-400">
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* User Detail */}
        {activeTab === "users" && selectedUser && (
          <div className="space-y-6" data-testid="admin-user-detail">
            <button onClick={() => setSelectedUser(null)} className="flex items-center gap-2 text-sky-400 hover:text-sky-300 text-sm">
              <ArrowLeft className="w-4 h-4" /> Back to Users
            </button>
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h2 className="text-xl font-light text-white mb-4" style={{ fontFamily: "Outfit" }}>{selectedUser.name || "User"}</h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                  <p className="text-lg font-light">{selectedUser.email}</p>
                  <p className="text-xs text-slate-400">Email</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                  <p className="text-lg font-light">{selectedUser.questionnaire_count || 0}</p>
                  <p className="text-xs text-slate-400">Questionnaires</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                  <p className="text-lg font-light">{selectedUser.game_count || 0}</p>
                  <p className="text-xs text-slate-400">Games Played</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                  <p className="text-lg font-light capitalize">
                    {selectedUser.subscription?.status === "active"
                      ? (selectedUser.subscription?.plan_id?.includes("diamond") ? "Diamond" : "Pro")
                      : "Free"}
                  </p>
                  <p className="text-xs text-slate-400">Subscription</p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {selectedUser.subscription?.status !== "active" ? (
                  <>
                    <Button onClick={() => grantSubscription(selectedUser.user_id, "yearly", 1)} className="bg-amber-500 hover:bg-amber-600 text-sm" data-testid="grant-pro-btn">
                      <Crown className="w-4 h-4 mr-1" /> Grant 1 Year Pro
                    </Button>
                    <Button onClick={() => grantSubscription(selectedUser.user_id, "diamond_yearly", 1)} className="bg-violet-500 hover:bg-violet-600 text-sm text-white" data-testid="grant-diamond-btn">
                      <Crown className="w-4 h-4 mr-1" /> Grant 1 Year Diamond
                    </Button>
                  </>
                ) : (
                  <div className="flex gap-2 flex-wrap">
                    {!selectedUser.subscription?.plan_id?.includes("diamond") && (
                      <Button onClick={() => grantSubscription(selectedUser.user_id, "diamond_yearly", 1)} className="bg-violet-500 hover:bg-violet-600 text-sm text-white" data-testid="upgrade-diamond-btn">
                        <Crown className="w-4 h-4 mr-1" /> Upgrade to Diamond
                      </Button>
                    )}
                    <Button variant="destructive" onClick={() => revokeSubscription(selectedUser.user_id)} className="text-sm" data-testid="revoke-sub-btn">
                      Revoke Subscription
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Activity Tab — Enhanced with visual metrics */}
        {activeTab === "activity" && (
          <div className="space-y-6" data-testid="admin-activity-content">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-light" style={{ fontFamily: "Outfit" }}>Activity Feed</h1>
              <Button variant="ghost" size="sm" onClick={fetchActivity} className="text-slate-400 hover:text-white">
                <RefreshCw className="w-4 h-4 mr-1" /> Refresh
              </Button>
            </div>

            {/* Activity type breakdown */}
            <div className="grid grid-cols-3 gap-4">
              {["signup", "questionnaire", "game"].map(type => {
                const count = activity.filter(a => a.type === type).length;
                const icons = { signup: Users, questionnaire: FileText, game: Gamepad2 };
                const colors = { signup: "text-sky-400", questionnaire: "text-cyan-400", game: "text-violet-400" };
                const Icon = icons[type];
                return (
                  <div key={type} className="bg-slate-800 rounded-xl p-4 border border-slate-700 text-center">
                    <Icon className={`w-5 h-5 ${colors[type]} mx-auto mb-2`} strokeWidth={1.5} />
                    <p className="text-2xl font-light text-white" style={{ fontFamily: "Outfit" }}>{count}</p>
                    <p className="text-xs text-slate-400 capitalize">{type}s (recent)</p>
                  </div>
                );
              })}
            </div>

            {/* Activity timeline */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
              {activity.map((a, idx) => (
                <div key={`${a.type}-${a.time}-${a.user}-${idx}`} className="flex items-center gap-4 px-5 py-3 border-b border-slate-700/50 last:border-0 hover:bg-slate-750">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    a.type === "signup" ? "bg-sky-400" : a.type === "questionnaire" ? "bg-cyan-400" : "bg-violet-400"
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{a.user || "Unknown"}</p>
                    <p className="text-xs text-slate-500">{a.detail}</p>
                  </div>
                  <span className="text-xs text-slate-600 font-mono truncate max-w-[140px]" title={a.user_id}>{a.user_id || ""}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    a.type === "signup" ? "bg-sky-500/20 text-sky-400" : a.type === "questionnaire" ? "bg-cyan-500/20 text-cyan-400" : "bg-violet-500/20 text-violet-400"
                  }`}>{a.type}</span>
                  <span className="text-xs text-slate-500 flex-shrink-0 w-32 text-right">{a.time ? new Date(a.time).toLocaleString() : ""}</span>
                </div>
              ))}
              {activity.length === 0 && <div className="px-5 py-8 text-center text-slate-500">No recent activity</div>}
            </div>
          </div>
        )}

        {/* Blog Tab */}
        {activeTab === "blog" && <BlogTab adminAxios={adminAxios} />}

        {/* Export Tab */}
        {activeTab === "export" && (
          <div className="space-y-6" data-testid="admin-export-content">
            <h1 className="text-2xl font-light" style={{ fontFamily: "Outfit" }}>Data Export</h1>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { key: "users", label: "Users", desc: "User accounts", icon: Users },
                { key: "questionnaires", label: "Questionnaires", desc: "Responses (decrypted)", icon: FileText },
                { key: "meditations", label: "AI Meditations", desc: "Generated sessions", icon: Heart },
                { key: "feedback", label: "Feedback", desc: "User feedback (decrypted)", icon: MessageCircle },
                { key: "contacts", label: "Contact Messages", desc: "Contact form (decrypted)", icon: Globe },
              ].map(col => (
                <div key={col.key} className="bg-slate-800 rounded-xl p-5 border border-slate-700">
                  <col.icon className="w-5 h-5 text-sky-400 mb-3" strokeWidth={1.5} />
                  <h3 className="text-sm font-medium text-white">{col.label}</h3>
                  <p className="text-xs text-slate-400 mb-4">{col.desc}</p>
                  <div className="flex gap-2">
                    <a href={`${API}/admin/export/${col.key}?format=csv`} className="text-xs px-3 py-1.5 bg-sky-500/20 text-sky-400 rounded-lg hover:bg-sky-500/30 transition-colors"
                      data-testid={`export-${col.key}-csv`}>CSV</a>
                    <a href={`${API}/admin/export/${col.key}?format=json`} className="text-xs px-3 py-1.5 bg-violet-500/20 text-violet-400 rounded-lg hover:bg-violet-500/30 transition-colors"
                      data-testid={`export-${col.key}-json`}>JSON</a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Nemoclaw Tab */}
        {activeTab === "nemoclaw" && <NemoclawTab adminAxios={adminAxios} />}
      </div>
    </div>
  );
};

export default AdminPanel;
